import React from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import type { NavigationProp, ParamListBase } from "@react-navigation/core";

import { formatDate, formatMoney } from "../util/gFormatting";
import { tOrder, tPaymentMethod, tVisualConnectionSource } from "tmt_api/src/types/types";
import * as orderDb from "src/db/order.db";
import { kRoute } from "src/kRoute";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { PdText } from "src/components/layout";
import { useIsMobile } from "src/hooks/ui";
import { tColumn, tColumnId, tRowInstance, useTable } from "src/hooks/table";
import { useSelector } from "react-redux";
import { generateCSVFromTableInstance, getHiddenColumnsFromConfig } from "src/util/table";
import { PdTable, PdTableCell, PdTableCellText, PdTableRow, PdTableSortableHeaders } from "src/components/v2/layout";
import { formatCurrency } from "tmt_api/src/util/formatters";
import { useAppDispatch } from "src/redux";
import { PdRow } from "src/components/v2/ui/core";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { PdCopy, PdSettings } from "src/components/Icons";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/v2/overlay";
import PopoverContentTableColumns from "src/components/connected/PopoverContentTableColumns";
import { tPressableState } from "src/components/types";

type tTransactionResponse = {
  orderId: string;
  orderNumber: string;
  accountSource: tVisualConnectionSource;
  orderSource: string;
  vendor: string;
  buyer: string;
  productTotal: number;
  total: number;
  discount: number;
  shipping: number;
  tmtCommissionFee: number;
  salesRepName: string;
  salesRepCommission: number;
  tmtServiceFee: number;
  brandPayout: number;
  mainTabPayout: number;
  paymentTerms: tPaymentMethod;
  transactionId: string;
  dateCreatedInMillis: number;
};

type tTableState = {
  data: tTransactionResponse[];
  isLoading: boolean;
};

const isRepOrder = (orderData: tOrder): boolean => {
  return !!orderData.cart.rep && orderData.cart.rep?.id !== "" && orderData.cart.rep?.vendorId !== "";
};

const isDirectOrder = (orderData: tOrder): boolean => {
  return orderData?.cart?.connectionSource === "Direct";
};

const isDirectRepOrder = (orderData: tOrder): boolean => {
  return orderData?.cart?.connectionSource === "Direct" && isRepOrder(orderData);
};

const isTheMainTabOrder = (orderData: tOrder): boolean => {
  return ["The Main Tab", "TMT"].includes(orderData?.cart?.connectionSource) && !isRepOrder(orderData);
};

const isTheMainTabRepOrder = (orderData: tOrder): boolean => {
  return ["The Main Tab", "TMT"].includes(orderData?.cart?.connectionSource) && isRepOrder(orderData);
};

const isDirectConnection = (orderData: tOrder): boolean => {
  return orderData.connection.connectionSource === "Direct";
};

const getDiscount = (orderData: tOrder): number => {
  return orderData?.cart?.discountCost || 0;
};

const getMainTabPayout = (orderData: tOrder): number => {
  const directOrder = isDirectOrder(orderData);
  const directRepOrder = isDirectRepOrder(orderData);
  const theMainTabOrder = isTheMainTabOrder(orderData);
  const theMainTabRepOrder = isTheMainTabRepOrder(orderData);
  const repOrder = isRepOrder(orderData);

  let tmtServiceFee = 0;
  let tmtCommission = 0;
  let tmtPayout = 0;

  if (orderData?.cart?.serviceFee && orderData?.cart?.serviceFee !== 0) {
    tmtServiceFee = orderData.cart.serviceFee;
  }

  if (directOrder && !repOrder) {
    tmtPayout = tmtServiceFee;
  } else if (directRepOrder) {
    tmtPayout = tmtServiceFee;
  } else if (theMainTabOrder) {
    if (orderData?.cart?.commissionFee) {
      tmtCommission = orderData.cart.commissionFee;
    }

    tmtPayout = tmtServiceFee + tmtCommission;
  } else if (theMainTabRepOrder) {
    tmtPayout = tmtServiceFee;
  }

  return tmtPayout;
};

const getTotal = (orderData: tOrder): number => {
  return orderData.subtotal + (orderData.discountCost ?? 0);
};

const getProductTotal = (orderData: tOrder): number => {
  return orderData.cart.totalPrice;
};

const getShipping = (orderData: tOrder): number => {
  if (orderData?.shippingCost) {
    return orderData?.shippingCost;
  }

  return 0;
};

const getTmtCommission = (orderData: tOrder): number => {
  const directOrder = isDirectOrder(orderData);
  const directRepOrder = isDirectRepOrder(orderData);
  const repOrder = isRepOrder(orderData);

  if ((directOrder && !repOrder) || directRepOrder) {
    return 0;
  } else {
    if (orderData?.cart?.commissionFee && orderData?.cart?.commissionFee !== 0) {
      return orderData.cart.commissionFee;
    }

    return 0;
  }
};

const getSalesRepName = (orderData: tOrder): string => {
  return isRepOrder(orderData) && orderData?.cart?.rep?.name ? orderData.cart.rep?.name : "";
};

const getSalesRepCommission = (orderData: tOrder): number => {
  const directRepOrder = isDirectRepOrder(orderData);
  const theMainTabRepOrder = isTheMainTabRepOrder(orderData);
  const productTotal = getProductTotal(orderData);

  if (!directRepOrder && !theMainTabRepOrder) {
    return 0;
  }

  if (directRepOrder || theMainTabRepOrder) {
    if (orderData?.cart.rep?.repPercent && orderData?.cart.rep?.repPercent !== 0) {
      return orderData.cart.rep.repPercent * productTotal;
    }
  }

  return 0;
};

const getTmtServiceFee = (orderData: tOrder): number => {
  if (orderData?.cart?.serviceFee && orderData?.cart?.serviceFee !== 0) {
    return orderData.cart.serviceFee;
  }

  return 0;
};

const getBrandPayout = (orderData: tOrder): number => {
  const mainTabPayout = getMainTabPayout(orderData);
  const productTotal = getProductTotal(orderData);
  const directRepOrder = isDirectRepOrder(orderData);
  const theMainTabRepOrder = isTheMainTabRepOrder(orderData);
  const shipping = getShipping(orderData);

  let salesRepCommission = 0;

  if (directRepOrder || theMainTabRepOrder) {
    salesRepCommission = getSalesRepCommission(orderData);
  }

  return productTotal - mainTabPayout - salesRepCommission + shipping;
};

const getOrderSource = (orderData: tOrder): string => {
  const directOrder = isDirectOrder(orderData);
  const repOrder = isRepOrder(orderData);
  const directConnection = isDirectConnection(orderData);
  const theMainTabOrder = isTheMainTabOrder(orderData);

  if (repOrder) return "Sales Rep";
  if (directOrder && repOrder) return "Sales Rep Order";
  if (directOrder && directConnection) return "Invited";
  if (directOrder) return "Brand Order";
  if (theMainTabOrder) return "The Main Tab";

  return "";
};

const getVendorByConnectionId = (orderData: tOrder): string => {
  const directOrder = isDirectOrder(orderData);
  const theMainTabOrder = isTheMainTabOrder(orderData);
  const theMainTabRepOrder = isTheMainTabRepOrder(orderData);
  const directRepOrder = isDirectRepOrder(orderData);
  const repOrder = isRepOrder(orderData);

  if ((directOrder && !repOrder) || directRepOrder || theMainTabOrder || theMainTabRepOrder) {
    return orderData.connection?.brand?.name || "";
  }

  return "";
};

const getVendor = (orderData: tOrder): string => {
  if (orderData?.cart?.connectionUid) {
    return getVendorByConnectionId(orderData);
  }

  return "";
};

const getBuyerByConnectionId = (orderData: tOrder): string => {
  const directOrder = isDirectOrder(orderData);
  const theMainTabOrder = isTheMainTabOrder(orderData);
  const theMainTabRepOrder = isTheMainTabRepOrder(orderData);
  const directRepOrder = isDirectRepOrder(orderData);
  const repOrder = isRepOrder(orderData);

  if ((directOrder && !repOrder) || directRepOrder || theMainTabOrder || theMainTabRepOrder) {
    return orderData.connection?.boutique?.name || "";
  }

  return "";
};

const getBuyer = (orderData: tOrder): string => {
  if (orderData?.cart?.connectionUid) {
    return getBuyerByConnectionId(orderData);
  }

  return "";
};

const mapTransaction = (order: tOrder): tTransactionResponse => {
  return {
    orderId: order.orderId,
    orderNumber: order.orderNumber,
    accountSource: order.connection.connectionSource === "TMT" ? "The Main Tab" : order.connection.connectionSource,
    orderSource: getOrderSource(order),
    vendor: getVendor(order),
    buyer: getBuyer(order),
    productTotal: getProductTotal(order),
    total: getTotal(order),
    discount: getDiscount(order),
    shipping: getShipping(order),
    tmtCommissionFee: getTmtCommission(order),
    salesRepName: getSalesRepName(order),
    salesRepCommission: getSalesRepCommission(order),
    tmtServiceFee: getTmtServiceFee(order),
    brandPayout: getBrandPayout(order),
    mainTabPayout: getMainTabPayout(order),
    paymentTerms: order.connection?.paymentMethod || "",
    transactionId: order.transactionId,
    dateCreatedInMillis: order.dateCreated.seconds * 1000,
  };
};

const P16_Transactions: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const rTableConfigs = useSelector((state) => state.TableConfigReducer.adminTransactionsData);
  const [tableState, setTableState] = React.useState<tTableState>({
    data: [],
    isLoading: true,
  });
  const tableInstance = useTable<tTransactionResponse>({
    data: tableState.data,
    columns,
    initialState: {
      sortedBy: "dateCreatedInMillis",
      sortDirection: "desc",
      hiddenColumns: getHiddenColumnsFromConfig(rTableConfigs),
    },
  });

  const handleOnPressCopyAsCSV = async () => {
    try {
      await navigator.clipboard.writeText(generateCSVFromTableInstance(tableInstance.rows, tableInstance.headers));
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const updateHiddenColumns = (newTableConfig: Partial<Record<tColumnId, boolean>>) => {
    const newHiddenColumns = getHiddenColumnsFromConfig(newTableConfig);
    tableInstance.setHiddenColumns(newHiddenColumns);
  };

  const handleOnRowPress = (item: tRowInstance<tTransactionResponse>) => {
    navigation.navigate(kRoute.P16_01, { id: item.orderId });
  };

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const query = await orderDb.queryOrder({ status: ["Awaiting Fulfillment", "Shipped"] });

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mapTransaction(doc.data()));
        setTableState({
          data,
          isLoading: false,
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, []);

  if (tableState.isLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdText variant="subheader" style={isMobile ? styles.mb4 : {}}>
        Transactions
      </PdText>
      {!isMobile && (
        <PdRow style={styles.tableOptions}>
          <PdButton
            colorScheme="gray"
            size="sm"
            rightIcon={<PdCopy style={styles.iconSm} />}
            onPress={handleOnPressCopyAsCSV}
          >
            <PdButtonLabel>Copy as CSV</PdButtonLabel>
          </PdButton>
          <PdPopover offset={22 + 2 * kStyleConst.kSpacing}>
            <PdPopoverTrigger>
              <PdButton colorScheme="gray" size="sm" rightIcon={<PdSettings style={styles.iconSm} />}>
                <PdButtonLabel>Configure Table</PdButtonLabel>
              </PdButton>
            </PdPopoverTrigger>
            <PdPopoverContent style={styles.configureTablePopoverContent}>
              <PopoverContentTableColumns
                columns={columns}
                tableConfigReduxKey="adminTransactionsData"
                onPressCheckbox={updateHiddenColumns}
              />
            </PdPopoverContent>
          </PdPopover>
        </PdRow>
      )}
      <PdTable
        ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
        data={tableInstance.rows}
        keyExtractor={(item) => item.orderId}
        renderItem={({ item }) => (
          <Pressable onPress={() => handleOnRowPress(item)}>
            {({ pressed, hovered }: tPressableState) => (
              <PdTableRow isHovered={hovered} isPressed={pressed}>
                {item.cells.map((cell) => (
                  <PdTableCell key={cell.uniqueId} style={cell.style}>
                    {cell.render("Cell")}
                  </PdTableCell>
                ))}
              </PdTableRow>
            )}
          </Pressable>
        )}
      />
    </View>
  );
};

export const P16 = React.memo(P16_Transactions);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  configureTablePopoverContent: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 1,
    left: -150,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
    right: 0,
  },
  flex1: {
    flex: 1,
  },
  iconSm: {
    height: 11,
    width: 11,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mobilePage: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 4,
  },
  page: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingVertical: kStyleConst.kSpacing * 8,
  },
  tableColumnLg: {
    width: 300,
  },
  tableColumnMd: {
    width: 200,
  },
  tableColumnSm: {
    width: 120,
  },
  tableOptions: {
    gap: kStyleConst.kSpacing * 2,
    marginBottom: kStyleConst.kSpacing * 2,
    marginLeft: "auto",
    zIndex: 1,
  },
});

const columns: tColumn<tTransactionResponse>[] = [
  {
    id: "orderNumber",
    header: "Order #",
    accessor: (originalRow) => originalRow.orderNumber,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "dateCreatedInMillis",
    header: "Date Created",
    accessor: (originalRow) => formatDate(originalRow.dateCreatedInMillis),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "accountSource",
    header: "Account Source",
    accessor: (originalRow) => originalRow.accountSource,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnMd,
  },
  {
    id: "orderSource",
    header: "Order Source",
    accessor: (originalRow) => originalRow.orderSource,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnMd,
  },
  {
    id: "vendor",
    header: "Vendor",
    accessor: (originalRow) => originalRow.vendor,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "buyer",
    header: "Buyer",
    accessor: (originalRow) => originalRow.buyer,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "productTotal",
    header: "Product Total",
    accessor: (originalRow) => formatMoney(originalRow.productTotal),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "total",
    header: "Total",
    accessor: (originalRow) => formatMoney(originalRow.total),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "discount",
    header: "Discount",
    accessor: (originalRow) => formatMoney(originalRow.discount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "shipping",
    header: "Shipping",
    accessor: (originalRow) => formatMoney(originalRow.shipping),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "tmtCommissionFee",
    header: "TMT Commission",
    accessor: (originalRow) => formatMoney(originalRow.tmtCommissionFee),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "salesRepName",
    header: "Sales Rep Name",
    accessor: (originalRow) => originalRow.salesRepName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "salesRepCommission",
    header: "Commission %",
    accessor: (originalRow) => formatCurrency(originalRow.salesRepCommission),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "tmtServiceFee",
    header: "TMT Service Fee",
    accessor: (originalRow) => formatMoney(originalRow.tmtServiceFee),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "brandPayout",
    header: "Brand Payout",
    accessor: (originalRow) => formatMoney(originalRow.brandPayout),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "mainTabPayout",
    header: "Main Tab Payout",
    accessor: (originalRow) => formatMoney(originalRow.mainTabPayout),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "paymentTerms",
    header: "Main Tab Payout",
    accessor: (originalRow) => originalRow.paymentTerms,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "transactionId",
    header: "Transaction Id",
    accessor: (originalRow) => originalRow.transactionId,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
];
